import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithErrorHandling } from "../baseQuery";

export const statsApi = createApi({
  reducerPath: "statsApi",
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ["Stats"],
  refetchOnFocus: true,
  refetchOnReconnect: true,

  endpoints: (builder) => ({
    // Get All Stats
    getStatsData: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        sortBy = "display_order",
        order = "asc",
      } = {}) => ({
        url: "/stats",
        params: { page, limit, search, sortBy, order },
      }),

      /* Normalize API response */
      transformResponse: (response) => ({
        stats: response?.data ?? [],
        total: response?.total ?? 0,
        page: response?.page ?? 1,
        limit: response?.limit ?? 10,
      }),

      /* Correct tag strategy */
      providesTags: (result) =>
        result?.stats?.length
          ? [
              ...result.stats.map(({ id }) => ({
                type: "Stats",
                id: Number(id),
              })),
              { type: "Stats", id: "LIST" },
            ]
          : [{ type: "Stats", id: "LIST" }],

      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: true,
    }),

    // Get Stats By ID
    getStatsById: builder.query({
      query: (id) => `/stats/${id}`,
      transformResponse: (response) =>
        response?.data ? response.data : response,
      providesTags: (result, error, id) => [{ type: "Stats", id }],
    }),

    // Create Stats
    createStats: builder.mutation({
      query: (data) => ({
        url: "/stats",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Stats", id: "LIST" }],
    }),

    // Update Stats
    updateStats: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/stats/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Stats", id },
        { type: "Stats", id: "LIST" },
      ],
    }),

    // Delete Stats
    deleteStats: builder.mutation({
      query: (id) => ({
        url: `/stats/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Stats", id: "LIST" }],
    }),

    /* Toggle Stats Status */
    toggleStatsStatus: builder.mutation({
      query: (id) => ({
        url: `/stats/${id}/toggle`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Stats", id },
        { type: "Stats", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetStatsDataQuery,
  useGetStatsByIdQuery,
  useCreateStatsMutation,
  useUpdateStatsMutation,
  useDeleteStatsMutation,
  useToggleStatsStatusMutation,
} = statsApi;
